import { isEqual, omit } from 'lodash-es'
import type { Snackbar } from '../../useSnackbarsStore.types'

interface GetUpdatedSnackbarsParams {
  snackbars: Snackbar[]
  newSnackbar: Snackbar
}

/**
 * Determines how to update the snackbars array when adding a new snackbar.
 *
 * Deduplication strategy (automatic based on variant + message hash):
 * - Check if a snackbar with the same auto-generated ID already exists
 * - If found:
 *   - Compare content (excluding key and id fields)
 *   - If identical: return unchanged array (prevents visual glitch)
 *   - If different: replace existing snackbar (last-wins)
 * - Otherwise: append new snackbar to array
 */
export const getUpdatedSnackbars = ({ snackbars, newSnackbar }: GetUpdatedSnackbarsParams): Snackbar[] => {
  const existingIndex = snackbars.findIndex((s) => s.id === newSnackbar.id)
  const existingSnackbar = snackbars[existingIndex]

  if (existingSnackbar) {
    if (isEqual(omit(existingSnackbar, 'id'), omit(newSnackbar, 'id'))) {
      return snackbars
    }

    return [...snackbars.slice(0, existingIndex), newSnackbar, ...snackbars.slice(existingIndex + 1)]
  }

  return [...snackbars, newSnackbar]
}
